import {
  addSample,
  addTransform,
  applyAssetChange,
  AssetChange,
  attachGroups,
  CasaSyncLine,
  Catalog,
  ConsoleState,
  detachGroup,
  DirectoryTenantPick,
  DocsSyncResult,
  Draft,
  emptyState,
  ErrorPolicy,
  executeTests,
  FieldMapping,
  findDraft,
  findSchema,
  findTransform,
  publish,
  publishBlockers,
  recordRun,
  removeDraft,
  removeRoute,
  removeSample,
  removeTransform,
  reorderVendor,
  rollback,
  RouteIngress,
  runCase,
  saveDraft,
  saveTransform,
  setRouteSchema,
  setRouteSink,
  Sink,
  syncFromCasa,
  syncFromDocs,
  QaCase,
  QaRun,
  TestCaseResult,
  TransformJob,
  updateRoute,
  validateErrorPolicy,
  validateIngress,
} from '../../domain';
import { apiFetch } from './http';

export interface Snapshot {
  state: ConsoleState;
  version: number;
}

/** How a test is sent: which version, as whom, and at which address. A key goes only as its hash. */
export interface TestOptions {
  mode?: 'draft' | 'live';
  request?: { method?: string; path?: string; query?: string; headers?: Record<string, string> };
  sender?: { keyHash: string } | { businessId: string; tenantId?: string };
  /** Only for a GET that Casa's backend answers. */
  send?: boolean;
}

/** Whether this server runs real QA bills, its past runs of an endpoint, and the cases it would send. */
export interface QaOverview {
  environment: string;
  runs: QaRun[];
  cases: Omit<QaCase, 'payload'>[];
  problem?: string;
}

export interface ConsoleClient {
  load(): Promise<Snapshot>;
  configure(version: number, change: AssetChange): Promise<Snapshot>;
  previewCasa(): Promise<{ lines: CasaSyncLine[] }>;
  previewDocs(): Promise<{ result: DocsSyncResult }>;
  /** Real runs on QA; `environment` says whether this server allows them. */
  qaRuns(routeId: string): Promise<QaOverview>;
  runQa(routeId: string): Promise<QaRun>;
  syncDocs(version: number): Promise<Snapshot>;
  syncCasa(version: number): Promise<Snapshot>;
  /** Moves a vendor one place in the authoring list. Never reaches the runtime. */
  reorderVendor(version: number, vendorId: string, direction: 'up' | 'down'): Promise<Snapshot>;
  attachGroups(version: number, vendorId: string, templateIds: string[], forAllShops: boolean): Promise<Snapshot>;
  detachGroup(version: number, groupId: string): Promise<Snapshot>;
  removeRoute(version: number, routeId: string): Promise<Snapshot>;
  setSchema(version: number, routeId: string, canonicalSchemaId: string): Promise<Snapshot>;
  setSink(version: number, routeId: string, sink: Sink): Promise<Snapshot>;
  setIngress(version: number, routeId: string, ingress: RouteIngress): Promise<Snapshot>;
  setErrorPolicy(version: number, routeId: string, errorPolicy: ErrorPolicy): Promise<Snapshot>;
  addSample(version: number, routeId: string, scope: string, name: string, payload: string): Promise<Snapshot>;
  removeSample(version: number, sampleId: string): Promise<Snapshot>;
  saveDraft(
    version: number,
    routeId: string,
    scope: string,
    mappings: FieldMapping[],
    authoring?: Pick<Draft, 'xslt' | 'resources' | 'mapperMetadata'>,
  ): Promise<Snapshot>;
  /** Removes a scope's own mapping, so a tenant scope reads the shared one again. */
  discardDraft(version: number, routeId: string, scope: string): Promise<Snapshot>;
  runTests(version: number, routeId: string, scope: string): Promise<Snapshot>;
  preview(
    routeId: string,
    scope: string,
    payload: string,
    authoring?: Pick<Draft, 'xslt' | 'resources' | 'mapperMetadata'>,
    options?: TestOptions,
  ): Promise<{ result: TestCaseResult; engine: 'preview' | 'camel-saxon' }>;
  publish(version: number, routeId: string, scope: string): Promise<Snapshot>;
  rollback(version: number, routeId: string, scope: string, toVersion: number): Promise<Snapshot>;
  /** A transformation no vendor owns: authored and tested here, run by another system. */
  addTransform(version: number, name: string, system: string): Promise<Snapshot>;
  saveTransform(version: number, id: string, patch: Partial<TransformJob>): Promise<Snapshot>;
  removeTransform(version: number, id: string): Promise<Snapshot>;
}

export class ConsoleRequestError extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    detail: string,
    readonly isStale: boolean,
  ) {
    super(detail);
    this.name = 'ConsoleRequestError';
  }
}

const scopeSegment = (scope: string) => encodeURIComponent(scope);

export class HttpConsoleClient implements ConsoleClient {
  /* No actor: the control plane takes that from the session, so it cannot be claimed. */
  constructor(private readonly baseUrl: string) {}

  private async request<T = Snapshot>(path: string, body?: unknown): Promise<T> {
    const response = await apiFetch(`${this.baseUrl}${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    const payload = await response.json().catch(() => undefined);

    if (!response.ok) {
      const detail = (payload as { detail?: string; title?: string } | undefined)?.detail ?? response.statusText;
      const title = (payload as { title?: string } | undefined)?.title ?? 'Request failed';
      throw new ConsoleRequestError(response.status, title, detail, response.status === 409);
    }

    return payload as T;
  }

  load = () => this.request('/api/v1/state');
  configure = (version: number, change: AssetChange) => this.request('/api/v1/configure', { version, change });
  previewCasa = () => this.request<{ lines: CasaSyncLine[] }>('/api/v1/directory/sync', { preview: true });
  syncCasa = (version: number) => this.request('/api/v1/directory/sync', { version });
  previewDocs = () => this.request<{ result: DocsSyncResult }>('/api/v1/catalog/sync', { preview: true });
  qaRuns = (routeId: string) => this.request<QaOverview>(`/api/v1/qa-runs?routeId=${encodeURIComponent(routeId)}`);
  runQa = (routeId: string) => this.request<QaRun>('/api/v1/qa-runs', { routeId });
  syncDocs = (version: number) => this.request('/api/v1/catalog/sync', { version });
  reorderVendor = (version: number, vendorId: string, direction: 'up' | 'down') =>
    this.request(`/api/v1/vendors/${vendorId}/order`, { version, direction });

  removeRoute = (version: number, routeId: string) => this.request(`/api/v1/routes/${routeId}/remove`, { version });
  attachGroups = (version: number, vendorId: string, templateIds: string[], forAllShops: boolean) =>
    this.request(`/api/v1/vendors/${vendorId}/groups`, { version, templateIds, forAllShops });
  detachGroup = (version: number, groupId: string) => this.request(`/api/v1/groups/${groupId}/detach`, { version });
  setSchema = (version: number, routeId: string, canonicalSchemaId: string) =>
    this.request(`/api/v1/routes/${routeId}/schema`, { version, canonicalSchemaId });
  setSink = (version: number, routeId: string, sink: Sink) =>
    this.request(`/api/v1/routes/${routeId}/sink`, { version, ...sink });
  setIngress = (version: number, routeId: string, ingress: RouteIngress) =>
    this.request(`/api/v1/routes/${routeId}/ingress`, { version, ingress });
  setErrorPolicy = (version: number, routeId: string, errorPolicy: ErrorPolicy) =>
    this.request(`/api/v1/routes/${routeId}/error-policy`, { version, errorPolicy });
  addSample = (version: number, routeId: string, scope: string, name: string, payload: string) =>
    this.request(`/api/v1/routes/${routeId}/scopes/${scopeSegment(scope)}/samples`, { version, name, payload });
  removeSample = (version: number, sampleId: string) => this.request(`/api/v1/samples/${sampleId}/delete`, { version });
  saveDraft = (
    version: number,
    routeId: string,
    scope: string,
    mappings: FieldMapping[],
    authoring?: Pick<Draft, 'xslt' | 'resources' | 'mapperMetadata'>,
  ) => this.request(`/api/v1/routes/${routeId}/scopes/${scopeSegment(scope)}/draft`, { version, mappings, authoring });

  discardDraft = (version: number, routeId: string, scope: string) =>
    this.request(`/api/v1/routes/${routeId}/scopes/${scopeSegment(scope)}/draft/discard`, { version });
  runTests = (version: number, routeId: string, scope: string) =>
    this.request(`/api/v1/routes/${routeId}/scopes/${scopeSegment(scope)}/tests`, { version });
  preview = (
    routeId: string,
    scope: string,
    payload: string,
    authoring?: Pick<Draft, 'xslt' | 'resources' | 'mapperMetadata'>,
    options?: TestOptions,
  ) =>
    this.request<{ result: TestCaseResult; engine: 'camel-saxon' }>(
      `/api/v1/routes/${routeId}/scopes/${scopeSegment(scope)}/preview`,
      { payload, authoring, ...options },
    );
  publish = (version: number, routeId: string, scope: string) =>
    this.request(`/api/v1/routes/${routeId}/scopes/${scopeSegment(scope)}/publish`, { version });
  rollback = (version: number, routeId: string, scope: string, toVersion: number) =>
    this.request(`/api/v1/routes/${routeId}/scopes/${scopeSegment(scope)}/rollback`, { version, toVersion });
  addTransform = (version: number, name: string, system: string) =>
    this.request('/api/v1/transforms', { version, name, system });
  saveTransform = (version: number, id: string, patch: Partial<TransformJob>) =>
    this.request(`/api/v1/transforms/${id}`, { version, patch });
  removeTransform = (version: number, id: string) => this.request(`/api/v1/transforms/${id}/remove`, { version });
}

/**
 * Applies the same domain reducers in the browser. Used by tests and by the
 * offline demo, so the console behaves identically without a control plane.
 */
export class MemoryConsoleClient implements ConsoleClient {
  private snapshot: Snapshot;

  constructor(initial?: ConsoleState) {
    this.snapshot = { state: initial ?? emptyState(), version: 1 };
  }

  private reject(status: number, title: string, detail: string): Promise<Snapshot> {
    return Promise.reject(new ConsoleRequestError(status, title, detail, false));
  }

  private commit(expected: number, next: ConsoleState): Promise<Snapshot> {
    if (expected !== this.snapshot.version) {
      return Promise.reject(
        new ConsoleRequestError(409, 'Stale write', 'Reload the configuration and apply your change again.', true),
      );
    }
    this.snapshot = { state: next, version: this.snapshot.version + 1 };

    return Promise.resolve(this.snapshot);
  }

  casa: DirectoryTenantPick[] = [];
  docs: Catalog = { source: 'openapi', groups: [] };

  previewDocs = () => Promise.resolve({ result: syncFromDocs(this.snapshot.state, this.docs).result });
  /* An in-memory workspace is never a QA server, so it keeps no runs and refuses to start one. */
  qaRuns = (): Promise<QaOverview> => Promise.resolve({ environment: 'memory', runs: [], cases: [] });
  runQa = (): Promise<QaRun> =>
    Promise.reject(new ConsoleRequestError(409, 'Conflict', 'Real runs are only for the QA server.', false));
  syncDocs = (version: number) => this.commit(version, syncFromDocs(this.snapshot.state, this.docs).state);

  previewCasa = () => Promise.resolve({ lines: syncFromCasa(this.snapshot.state, this.casa).lines });
  syncCasa = (version: number) => this.commit(version, syncFromCasa(this.snapshot.state, this.casa).state);
  load = () => Promise.resolve(this.snapshot);
  configure = (version: number, change: AssetChange) => {
    try {
      return this.commit(version, applyAssetChange(this.snapshot.state, change));
    } catch (error) {
      return this.reject(422, 'Invalid configuration', error instanceof Error ? error.message : 'Invalid change.');
    }
  };
  reorderVendor = (v: number, vendorId: string, direction: 'up' | 'down') => {
    if (!this.snapshot.state.vendors.some((vendor) => vendor.id === vendorId)) {
      return this.reject(404, 'Not found', `No vendor ${vendorId}.`);
    }
    return this.commit(v, reorderVendor(this.snapshot.state, vendorId, direction));
  };
  removeRoute = (v: number, routeId: string) => {
    if (!this.snapshot.state.routes.some((route) => route.id === routeId)) {
      return this.reject(404, 'Not found', `No route ${routeId}.`);
    }
    return this.commit(v, removeRoute(this.snapshot.state, routeId));
  };
  attachGroups = (v: number, vendorId: string, templateIds: string[], forAllShops: boolean) => {
    const { state } = this.snapshot;
    if (!state.vendors.some((vendor) => vendor.id === vendorId)) {
      return this.reject(404, 'Not found', `No vendor ${vendorId}.`);
    }
    try {
      return this.commit(v, attachGroups(state, vendorId, templateIds, forAllShops));
    } catch (cause) {
      return this.reject(400, 'Invalid request', cause instanceof Error ? cause.message : 'Not added.');
    }
  };
  detachGroup = (v: number, groupId: string) => {
    if (!this.snapshot.state.groups.some((group) => group.id === groupId)) {
      return this.reject(404, 'Not found', `No group ${groupId}.`);
    }
    return this.commit(v, detachGroup(this.snapshot.state, groupId));
  };
  setSchema = (v: number, routeId: string, canonicalSchemaId: string) => {
    const { state } = this.snapshot;
    if (!state.routes.some((route) => route.id === routeId)) {
      return this.reject(404, 'Not found', `No route ${routeId}.`);
    }
    if (!state.schemas.some((schema) => schema.id === canonicalSchemaId)) {
      return this.reject(404, 'Not found', `No canonical contract ${canonicalSchemaId}.`);
    }
    return this.commit(v, setRouteSchema(state, routeId, canonicalSchemaId));
  };
  setIngress = (v: number, routeId: string, ingress: RouteIngress) => {
    const { state } = this.snapshot;
    const route = state.routes.find((candidate) => candidate.id === routeId);
    if (!route) return this.reject(404, 'Not found', `No route ${routeId}.`);
    const issues = validateIngress(state, route, ingress);
    if (issues.length > 0) {
      return this.reject(422, 'Not configurable', issues.map((issue) => issue.message).join(' '));
    }
    return this.commit(v, updateRoute(state, routeId, { ingress }));
  };
  setErrorPolicy = (v: number, routeId: string, errorPolicy: ErrorPolicy) => {
    const { state } = this.snapshot;
    if (!state.routes.some((candidate) => candidate.id === routeId)) {
      return this.reject(404, 'Not found', `No route ${routeId}.`);
    }
    const issues = validateErrorPolicy(errorPolicy);
    if (issues.length > 0) {
      return this.reject(422, 'Not configurable', issues.map((issue) => issue.message).join(' '));
    }
    return this.commit(v, updateRoute(state, routeId, { errorPolicy }));
  };
  setSink = (v: number, routeId: string, sink: Sink) => {
    if (!this.snapshot.state.routes.some((route) => route.id === routeId)) {
      return this.reject(404, 'Not found', `No route ${routeId}.`);
    }
    if (sink.kind === 'http' && !/^https?:\/\//.test(sink.target)) {
      return this.reject(400, 'Invalid request', 'An http sink target must be an absolute URL.');
    }
    return this.commit(v, setRouteSink(this.snapshot.state, routeId, sink));
  };
  addSample = (v: number, routeId: string, scope: string, name: string, payload: string) => {
    if (!this.snapshot.state.routes.some((route) => route.id === routeId)) {
      return this.reject(404, 'Not found', `No route ${routeId}.`);
    }
    try {
      JSON.parse(payload);
    } catch {
      return this.reject(400, 'Invalid request', '"payload" must be valid JSON.');
    }
    return this.commit(
      v,
      addSample(this.snapshot.state, { id: `sample-${Date.now().toString(36)}`, routeId, scope, name, payload }),
    );
  };
  removeSample = (v: number, sampleId: string) => {
    if (!this.snapshot.state.samples.some((sample) => sample.id === sampleId)) {
      return this.reject(404, 'Not found', `No sample ${sampleId}.`);
    }
    return this.commit(v, removeSample(this.snapshot.state, sampleId));
  };
  saveDraft = (
    v: number,
    routeId: string,
    scope: string,
    mappings: FieldMapping[],
    authoring?: Pick<Draft, 'xslt' | 'resources' | 'mapperMetadata'>,
  ) => this.commit(v, saveDraft(this.snapshot.state, routeId, scope, mappings, authoring));
  discardDraft = (v: number, routeId: string, scope: string) =>
    this.commit(v, removeDraft(this.snapshot.state, routeId, scope));
  preview = async (routeId: string, scope: string, payload: string) => {
    const state = this.snapshot.state;
    const draft = findDraft(state, routeId, scope);
    if (draft?.xslt)
      throw new Error('Native stylesheet verification needs the control plane. Connect it to run this draft.');
    const route = state.routes.find((candidate) => candidate.id === routeId);
    return {
      result: runCase(
        { id: 'preview', name: 'Sandbox', routeId, scope, payload },
        draft?.mappings ?? [],
        findSchema(state, route?.canonicalSchemaId),
      ),
      engine: 'preview' as const,
    };
  };
  runTests = (v: number, routeId: string, scope: string) => {
    if (findDraft(this.snapshot.state, routeId, scope)?.xslt)
      return this.reject(
        422,
        'Connect the control plane',
        'Native stylesheet verification requires Camel/Saxon. Connect the control plane to verify and publish this draft.',
      );
    const run = executeTests(this.snapshot.state, routeId, scope);
    if (!run) {
      return Promise.reject(new ConsoleRequestError(400, 'No draft', 'Save a draft before running tests.', false));
    }
    return this.commit(v, recordRun(this.snapshot.state, run));
  };
  publish = (v: number, routeId: string, scope: string) => {
    const blockers = publishBlockers(this.snapshot.state, routeId, scope);
    if (blockers.length > 0) {
      return Promise.reject(
        new ConsoleRequestError(422, 'Not publishable', blockers.map((blocker) => blocker.message).join(' '), false),
      );
    }
    return this.commit(v, publish(this.snapshot.state, routeId, scope, 'console-user'));
  };
  rollback = (v: number, routeId: string, scope: string, toVersion: number) =>
    this.commit(v, rollback(this.snapshot.state, routeId, scope, toVersion));
  addTransform = (v: number, name: string, system: string) => {
    const next = addTransform(this.snapshot.state, name, system);
    if ((next.transforms?.length ?? 0) === (this.snapshot.state.transforms?.length ?? 0)) {
      return this.reject(
        400,
        'Invalid request',
        `A transformation named "${name}" already exists, or the name is unusable.`,
      );
    }
    return this.commit(v, next);
  };
  saveTransform = (v: number, id: string, patch: Partial<TransformJob>) => {
    if (!findTransform(this.snapshot.state, id)) return this.reject(404, 'Not found', `No transformation ${id}.`);
    return this.commit(v, saveTransform(this.snapshot.state, id, patch));
  };
  removeTransform = (v: number, id: string) => {
    if (!findTransform(this.snapshot.state, id)) return this.reject(404, 'Not found', `No transformation ${id}.`);
    return this.commit(v, removeTransform(this.snapshot.state, id));
  };
}

/** Where the control plane is, for URLs the vendor is given. Empty when running offline. */
/** Where `yarn start` looks for the control plane when VITE_PLATFORM_API says nothing. */
const DEV_API_BASE = 'http://localhost:8787';

/** Where the data plane serves vendor traffic, which is a different host to the control plane. */
const DEV_PLANE_BASE = 'http://localhost:8085';

export const planeBaseUrl = (): string => {
  const configured = import.meta.env?.VITE_PLATFORM_PLANE as string | undefined;
  if (configured !== undefined) return configured;

  return import.meta.env?.MODE === 'dev' ? DEV_PLANE_BASE : '';
};

export const apiBaseUrl = (): string => {
  const configured = import.meta.env?.VITE_PLATFORM_API as string | undefined;
  if (configured !== undefined) return configured;

  return import.meta.env?.MODE === 'dev' ? DEV_API_BASE : '';
};

let offlineClient: MemoryConsoleClient | undefined;

export const createClient = (): ConsoleClient => {
  const baseUrl = apiBaseUrl();
  if (baseUrl) return new HttpConsoleClient(baseUrl);

  /*
   * Said out loud, because the alternative looks like it works. Without an address the console
   * runs against a store in this tab: every change appears to save and none of it leaves the
   * browser. The build refuses this for a production bundle; a bundle built some other way at
   * least says so.
   */
  if (import.meta.env?.MODE !== 'dev') {
    console.error(
      'VITE_PLATFORM_API was not set when this console was built, so nothing here is being saved. ' +
        'Rebuild with the control plane address.',
    );
  }

  return (offlineClient ??= new MemoryConsoleClient(emptyState()));
};
